// Web Push for the installed dashboard. Exposed as window.bookifyPush so
// Alpine components (the dashboard banner, the settings card) can ask
// where this device stands and switch notifications on or off.
//
// iOS only allows push for a page installed to the home screen, and only
// asks permission from inside that install — so "installed" matters as
// much as "supported" here.

const SW_URL = '/booking-app-sw.js';

function urlBase64ToUint8Array(base64) {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

function csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
}

// Desktop browsers may tuck the permission request behind a small bell in
// the address bar ("quiet" notification UI) and leave requestPermission()
// pending until the user finds it. Nothing about that resolves on its own,
// so every step that can wait on the user or the push service gets a
// deadline, and the caller gets told which one ran out.
function withTimeout(promise, ms, label) {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error(label + '-timeout')), ms);
        promise.then((value) => { clearTimeout(timer); resolve(value); }, (error) => { clearTimeout(timer); reject(error); });
    });
}

const push = {
    supported() {
        return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    },

    isIos() {
        return /iPad|iPhone|iPod/.test(navigator.userAgent)
            || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    },

    installed() {
        return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    },

    // iOS Safari has PushManager only inside an installed app, so a plain
    // Safari tab reports "not supported" — which is exactly the case where
    // the fix is to install first.
    needsInstallFirst() {
        return this.isIos() && !this.installed();
    },

    permission() {
        return 'Notification' in window ? Notification.permission : 'unsupported';
    },

    async registration() {
        const existing = await navigator.serviceWorker.getRegistration('/');
        return existing ?? navigator.serviceWorker.register(SW_URL, { scope: '/' });
    },

    async current() {
        if (!this.supported()) return null;
        const registration = await this.registration();
        return registration.pushManager.getSubscription();
    },

    async enable(vapidPublicKey) {
        if (!this.supported()) throw new Error('unsupported');

        let permission = Notification.permission;

        if (permission !== 'granted') {
            try {
                permission = await withTimeout(Notification.requestPermission(), 25000, 'permission');
            } catch (error) {
                // The prompt may have been answered in the address bar after all.
                permission = Notification.permission;
                if (permission !== 'granted') throw error;
            }
        }

        if (permission !== 'granted') throw new Error('denied');

        const registration = await withTimeout(this.registration(), 15000, 'worker');
        await withTimeout(navigator.serviceWorker.ready, 15000, 'worker');

        const subscription = await withTimeout(registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        }), 20000, 'subscribe');

        const json = subscription.toJSON();
        const response = await fetch('/push/subscriptions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf(), Accept: 'application/json' },
            body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys, contentEncoding: 'aes128gcm' }),
        });

        if (!response.ok) throw new Error('server');
        return response.json();
    },

    async disable() {
        const subscription = await this.current();
        if (!subscription) return;

        await fetch('/push/subscriptions', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf(), Accept: 'application/json' },
            body: JSON.stringify({ endpoint: subscription.endpoint }),
        });
        await subscription.unsubscribe();
    },
};

window.bookifyPush = push;

// Register the worker early on signed-in pages so an install prompt can
// appear and push has somewhere to land. Idempotent.
if (push.supported() && document.querySelector('meta[name="bookify-app"]')) {
    navigator.serviceWorker.register(SW_URL, { scope: '/' }).catch(() => {});
}
